import React from 'react';
import { array, bool } from 'prop-types';
import css from './SeoDiscovery.module.css';
import { paragraphsFromText, sanitizeDisplayCopy } from './seoCopyDisplay';

const normalizeItems = items =>
  (items || [])
    .map(item => {
      if (typeof item === 'string') {
        return { label: sanitizeDisplayCopy(item).replace(/\s+/g, ' ').trim(), detail: null };
      }
      return {
        label: sanitizeDisplayCopy(item?.label || item?.name || '')
          .replace(/\s+/g, ' ')
          .trim(),
        detail: sanitizeDisplayCopy(item?.detail || item?.description || '').trim() || null,
      };
    })
    .filter(item => item.label);

/** Only split when the body already has real markdown list lines — never invent from prose. */
const splitMarkdownList = text => {
  const cleaned = sanitizeDisplayCopy(text);
  if (!/(^|\n)\s*[-•]\s+\S/.test(cleaned)) {
    return { body: cleaned, items: [] };
  }

  const normalized = cleaned.replace(/\s+[-•]\s+(?=[^:\n]{1,80}:)/g, '\n- ');
  const firstMarker = normalized.search(/(^|\n)\s*[-•]\s+/);
  if (firstMarker === -1) {
    return { body: cleaned, items: [] };
  }

  const body = normalized.slice(0, firstMarker).trim();
  const itemLines = normalized
    .slice(firstMarker)
    .split('\n')
    .map(line => line.replace(/^\s*[-•]\s*/, '').trim())
    .filter(Boolean);
  const items = itemLines.map(line => {
    const [label, ...detailParts] = line.split(':');
    return {
      label: label.trim(),
      detail: detailParts.join(':').trim() || null,
    };
  });

  return { body, items: normalizeItems(items) };
};

const isStackedItem = item => {
  const detail = item.detail || '';
  return detail.length > 120 || detail.includes('\n');
};

const SeoContentSections = props => {
  const { blocks, suppressFirstHeading, excludeHero } = props;
  const sections = (blocks || []).filter(
    b =>
      (b.blockType === 'section' || b.blockType === 'hero') &&
      !(excludeHero && b.blockType === 'hero')
  );
  return (
    <>
      {sections.map((block, index) => {
        const { heading, body, summary, items } = block.content || {};
        const parsed = splitMarkdownList(body || summary);
        const paragraphs = paragraphsFromText(parsed.body);
        const normalizedItems = normalizeItems(items);
        const listItems = normalizedItems.length ? normalizedItems : parsed.items;
        if (!paragraphs.length && !heading && !listItems.length) {
          return null;
        }
        return (
          <section key={block.blockId} className={css.section}>
            {heading && !(suppressFirstHeading && index === 0) ? (
              <h2>{heading}</h2>
            ) : null}
            {paragraphs.map((para, paraIndex) => (
              <p key={`${block.blockId}-p-${paraIndex}`}>{para}</p>
            ))}
            {listItems.length ? (
              <ul className={css.sectionList}>
                {listItems.map((item, itemIndex) => {
                  const stacked = isStackedItem(item);
                  return (
                    <li
                      key={`${block.blockId}-item-${itemIndex}`}
                      className={
                        stacked ? `${css.sectionListItem} ${css.sectionListItemStacked}` : css.sectionListItem
                      }
                    >
                      <span className={css.sectionListLabel}>{item.label}</span>
                      {item.detail ? (
                        <div className={css.sectionListDetail}>
                          {paragraphsFromText(item.detail).map((para, paraIndex) => (
                            <p key={`${block.blockId}-item-${itemIndex}-p-${paraIndex}`}>{para}</p>
                          ))}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        );
      })}
    </>
  );
};

SeoContentSections.propTypes = {
  blocks: array,
  suppressFirstHeading: bool,
  excludeHero: bool,
};

export default SeoContentSections;
